import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApplicationApiService } from '@workflow/ecp-workflow/dynamic-app/service/application-api/application-api.service';
import { SharedUiLibraryModule } from '@cudirect/shared-ui-library';
import { SawDust } from '@cudirect/sawdust/sawdust';
import { WorkflowLoaderService } from '@shared/service/workflow-loader.service';
import { CommonModule } from '@angular/common';
interface PrefillVerifyState {
  loading: boolean;
  success: boolean;
  error?: string;
  sessionId: string;
  index: number;
  verifyCode?: string;
}

@Component({
  selector: 'app-prefill-verification',
  standalone: true,
  imports: [CommonModule, SharedUiLibraryModule],
  templateUrl: './prefill-verification.component.html',
  styleUrls: ['./prefill-verification.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PrefillVerificationComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly applicationApiService = inject(ApplicationApiService);
  private readonly loader = inject(WorkflowLoaderService);
  private readonly log = inject(SawDust);
  private logContext(additional: Record<string, unknown> = {}) {
    return { component: 'PrefillVerificationComponent', ...additional };
  }

  state = signal<PrefillVerifyState>({ loading: true, success: false, sessionId: '', index: 1 });

  ngOnInit(): void {
    const params = this.route.snapshot.params as { sessionId?: string; applicantIndex?: string };
    const sessionId = params.sessionId ?? '';
    const index = parseInt(params.applicantIndex ?? '0', 10);
    const verifyCode = this.route.snapshot.queryParamMap.get('vfp') || '';

    this.log.info('Prefill verification initialized', this.logContext({ sessionIdPresent: !!sessionId, index }));

    // Fallback: missing/invalid params -> redirect to 404 page
    if (!sessionId || verifyCode.length === 0) {
      this.log.warn('Prefill verification missing required params; redirecting to 404', this.logContext({
        sessionIdPresent: !!sessionId,
        verifyCodeLength: verifyCode.length
      }));
      // Redirect user to 404 error route; keep minimal state update for potential template safety
      this.state.update((s) => ({
        ...s,
        loading: false,
        success: false,
        error: 'Invalid or expired link.',
        sessionId,
        verifyCode,
        index
      }));
      this.router.navigate(['/error/404']);
      return;
    }

    this.log.info('Alloy prefill link received', this.logContext({ sessionId, index }));

    // Start full-screen orbital loader (no message, no segments)
    this.loader.start(undefined, { animationId: 'default', overlayMode: 'full' });

    // Invoke backend verification
    const payload = {
      sessionId,
      index,
      verifyCode
    } as const;

    this.applicationApiService.verifyPrefillLink(payload).subscribe({
      next: (res) => {
        const success = !!res?.isSuccess;
        this.loader.clear();
        this.state.update((s) => ({
          ...s,
          loading: false,
          success,
          error: success ? undefined : res?.message || 'Invalid or expired link.',
          sessionId,
          verifyCode,
          index
        }));
        this.log.info('Prefill verification completed', this.logContext({ sessionId, success, index }));
      },
      error: (err) => {
        this.log.error('Prefill verification failed', err, this.logContext({ sessionId, index }));
        this.loader.clear();
        this.state.update((s) => ({
          ...s,
          loading: false,
          success: false,
          error: 'Invalid or expired link.',
          sessionId,
          verifyCode,
          index
        }));
      }
    });
  }

  /**
   * Todo: Fix ths
   */
  closeTab(): void {
    try {
      window.open('', '_self');
    } catch (_) {}
    window.close();

    // Secondary attempt: use opener if available (some browsers allow self-null then close)
    if (!window.closed) {
      try {
        // @ts-ignore
        window.opener = null;
        // @ts-ignore
        window.open('', '_self');
        window.close();
      } catch (_) {}
    }

    setTimeout(() => {
      if (!window.closed && !document.hidden) {
        this.log.warn('Unable to close browser tab automatically; redirecting to home', this.logContext());
        this.router.navigate(['/']);
      }
    }, 200);
  }
}
